import { atlasNow, rowOfClip } from './atlas';
import { REST_SRC } from './clips';

/* Buffy's painter: one <canvas> per player (owner report 2026-09-29: soft
   outlines and "melting" glasses at Windows display scaling).

   The canvas backing store is 64 x dev DEVICE pixels (dev = device px per art
   pixel, a whole number) and it is shown at exactly that many device pixels,
   so the browser never rescales it. Its CSS box is 64 x dev CSS px scaled by
   1 / devicePixelRatio (canvasStyle): a transform is exact, while a layout
   width such as 204.8 px (256 device px at 125 %) is rounded to 1/64 px and
   would resample the image by a hair. Each frame is copied from the pre-decoded
   atlas with image smoothing off: every art pixel becomes a dev x dev block
   of one exact palette color, in every browser. A frame is drawn only when
   the clip, frame, mirror or scale changes. Before the atlas is ready the
   rest pose is drawn from rest.png. */

const CELL = 64;
let restImg = null;

/* The canvas's inline style for a device scale (both players render it). */
export function canvasStyle(dev, dpr) {
  const size = CELL * dev;
  return {
    width: size,
    height: size,
    transform: dpr === 1 ? undefined : `scale(${1 / dpr})`,
    transformOrigin: '0 0',
  };
}

function restImage() {
  if (restImg || typeof Image === 'undefined') return restImg;
  restImg = new Image();
  restImg.src = REST_SRC;
  return restImg;
}

export function createPainter(canvas) {
  const ctx = canvas && canvas.getContext ? canvas.getContext('2d') : null;
  let dev = 0;
  let key = '';
  return {
    /* Set the device scale; returns the CSS cell size (64 x dev / dpr). */
    scale(devScale, dpr) {
      if (devScale !== dev) {
        dev = devScale;
        canvas.width = CELL * dev;
        canvas.height = CELL * dev;
        key = '';
      }
      const st = canvasStyle(dev, dpr);
      canvas.style.width = `${st.width}px`;
      canvas.style.height = `${st.height}px`;
      canvas.style.transform = st.transform || '';
      canvas.style.transformOrigin = st.transformOrigin;
      return (CELL * dev) / dpr;
    },
    /* Paint `frame` of clip `clipName` (sheet row from the atlas). */
    draw(clipName, frame, mirror) {
      if (!ctx || !dev) return;
      const atlas = atlasNow();
      const rest = atlas ? null : restImage();
      const ready = atlas ? 'A' : `R${rest && rest.complete ? 1 : 0}`;
      const k = `${ready}|${clipName}|${frame}|${mirror ? 1 : 0}|${dev}`;
      if (k === key) return;
      key = k;
      const size = CELL * dev;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, size, size);
      ctx.imageSmoothingEnabled = false;
      if (mirror) ctx.setTransform(-1, 0, 0, 1, size, 0);
      if (atlas) {
        ctx.drawImage(atlas.img, frame * CELL, rowOfClip(atlas, clipName) * CELL, CELL, CELL, 0, 0, size, size);
      } else if (rest && rest.complete && rest.naturalWidth) {
        ctx.drawImage(rest, 0, 0, CELL, CELL, 0, 0, size, size);
      } else {
        key = ''; // try again next frame
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      canvas.setAttribute('data-clip', clipName);
      canvas.setAttribute('data-frame', String(frame));
    },
    /* Force the next draw (after the atlas arrives or a resync). */
    invalidate() { key = ''; },
  };
}
